import { useCallback, useState } from "react";
import { toast } from "sonner";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@components/ui/spinner";
import { Label } from "@components/ui/label";
import { SiteNameDropdown, type ExistingDomain } from "./SiteNameDropdown";
import { ClaimSubdomainModal } from "./ClaimSubdomainModal";
import { ReleaseDomainModal } from "./ReleaseDomainModal";
import { useDomainPickerData, useSelectionInit } from "./hooks";
import { useCreateDomain, useReleaseDomain } from "~/api/domainContext.hooks";
import { useDnsVerification } from "~/hooks/useDnsVerification";
import { useMinimumDuration } from "~/hooks/useMinimumDuration";
import { useWebsiteId } from "~/stores/projectStore";
import { isPlatformDomain } from "~/lib/domain";

// ============================================================================
// Types
// ============================================================================

export type DomainOrigin = "existing" | "suggestion" | "user-input";

export interface DomainAssignment {
  domain: string;
  origin: DomainOrigin;
  existingDomainId?: number;
}

export interface DomainPickerProps {
  /** Current selection (controlled). */
  selection: DomainAssignment | null;
  /** Called whenever the saved selection changes. */
  onSelectionChange: (selection: DomainAssignment | null) => void;
  /** Optional controlled-open for the dropdown (e.g. the Deploy action pops it when no domain is set). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Compact domain picker — a site-name dropdown that sits inline in the build
 * toolbar next to the PagePicker (the two read as the same component family).
 *
 * The claim-confirm modal fires at *selection* time: picking (or typing +
 * Enter) a brand-new launch10 subdomain prompts the modal, and confirming
 * claims it immediately (consuming a credit). Existing and custom domains save
 * straight away. After any successful save, `onSelectionChange` reports the
 * persisted assignment so callers can react (e.g. enable the deploy hand-off,
 * or collect the chosen domain for the chat agent).
 */
export function DomainPicker({ selection, onSelectionChange, open, onOpenChange }: DomainPickerProps) {
  const websiteId = useWebsiteId();
  const { context, recommendations, isOutOfCredits, creditsRemaining, isUnlimited, isLoading } =
    useDomainPickerData(websiteId ?? undefined);

  const createDomain = useCreateDomain();
  const releaseDomain = useReleaseDomain();

  // A pending claim awaiting the user's confirmation in the modal. Holds the
  // would-be selection so we only commit it once they say yes.
  const [pendingClaim, setPendingClaim] = useState<{ domain: string; origin: DomainOrigin } | null>(
    null
  );

  // A claim the user is about to release (give up entirely).
  const [pendingRelease, setPendingRelease] = useState<ExistingDomain | null>(null);

  // Pre-select from the website's existing assignment (saved) or the top AI
  // recommendation (unsaved — callers stay "unconfirmed" until the user picks).
  useSelectionInit({
    isLoading,
    assignedUrl: context?.assigned_url ?? null,
    topRecommendation: recommendations?.topRecommendation,
    onSelectionChange,
    markPersisted: () => {},
  });

  // Only a *persisted* selection (claimed/assigned, so it has an id) counts as
  // "chosen" in the trigger. A bare AI pre-fill must not read as a committed
  // choice — the user hasn't picked anything yet, and deploy stays gated.
  const isPersisted = !!selection?.existingDomainId;
  const selectedDomain = isPersisted ? (selection?.domain ?? null) : null;

  // DNS status for an assigned custom domain (platform subdomains are instant).
  const customDomainId =
    selection && !isPlatformDomain(selection.domain) ? (selection.existingDomainId ?? null) : null;
  const {
    isVerified: isDnsVerified,
    isPending: isDnsPending,
    isFailed: isDnsFailed,
    isFetching: isDnsFetching,
    error: dnsError,
  } = useDnsVerification(customDomainId);
  const isShowingDnsCheck = useMinimumDuration(isDnsFetching, 3000);

  /** Persist a selection (claim a new subdomain, or assign an existing/custom domain). */
  const save = useCallback(
    async (domain: string, origin: DomainOrigin, existingDomainId?: number) => {
      if (!websiteId) return;
      try {
        const result = await createDomain.mutateAsync({
          domain,
          websiteId,
          isPlatformSubdomain: isPlatformDomain(domain),
        });
        onSelectionChange({
          domain,
          origin: existingDomainId ? origin : "existing",
          existingDomainId: result.domain.id,
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to connect domain");
      }
    },
    [websiteId, createDomain, onSelectionChange]
  );

  // SiteNameDropdown selection / input-submit handler. A brand-new launch10
  // subdomain prompts the claim modal first; everything else saves directly.
  const handleSelect = useCallback(
    (domain: string, origin: DomainOrigin, existingDomainId?: number) => {
      const needsClaim = !existingDomainId && isPlatformDomain(domain);
      if (needsClaim) {
        setPendingClaim({ domain, origin });
        return;
      }
      void save(domain, origin, existingDomainId);
    },
    [save]
  );

  const handleClaimConfirm = useCallback(() => {
    if (!pendingClaim) return;
    const { domain, origin } = pendingClaim;
    setPendingClaim(null);
    void save(domain, origin);
  }, [pendingClaim, save]);

  const handleClaimClose = useCallback(() => setPendingClaim(null), []);

  const handleReleaseConfirm = useCallback(async () => {
    if (!pendingRelease || !websiteId) return;
    const released = pendingRelease;
    try {
      await releaseDomain.mutateAsync({ domainId: released.id, websiteId });
      // If we just released the domain the picker was showing, clear it.
      if (selection?.domain === released.domain) onSelectionChange(null);
      setPendingRelease(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to release domain");
    }
  }, [pendingRelease, websiteId, releaseDomain, selection?.domain, onSelectionChange]);

  const handleReleaseClose = useCallback(() => setPendingRelease(null), []);

  return (
    <div className="flex w-72 shrink-0 flex-col gap-1">
      {/* Label row — DNS status (custom domains only) sits inline on the right
          so it never adds a row below the dropdown and throws off the toolbar's
          vertical alignment with the Page picker / Deploy pill. */}
      <div className="flex min-h-4 items-center justify-between gap-2">
        <Label className="text-xs font-medium text-base-400">Your site name</Label>
        {customDomainId && (
          <div data-testid="dns-verification-status" className="min-w-0">
            {isShowingDnsCheck ? (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <Spinner className="size-3 shrink-0" />
                <span className="truncate">Checking…</span>
              </div>
            ) : isDnsVerified ? (
              <div className="flex items-center gap-1 text-xs text-success-500">
                <CheckCircleIcon className="size-3 shrink-0" />
                <span className="truncate">Domain set up</span>
              </div>
            ) : isDnsFailed ? (
              <div
                className="flex items-center gap-1 text-xs text-destructive"
                title={`DNS verification failed: ${dnsError}`}
              >
                <XCircleIcon className="size-3 shrink-0" />
                <span className="truncate">DNS check failed</span>
              </div>
            ) : isDnsPending ? (
              <div
                className="flex items-center gap-1 text-xs text-red-400"
                title="Not ready yet. We'll keep checking."
              >
                <XCircleIcon className="size-3 shrink-0" />
                <span className="truncate">Not ready yet</span>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <SiteNameDropdown
        recommendations={recommendations}
        context={context}
        selectedDomain={selectedDomain}
        isOutOfCredits={isOutOfCredits}
        onSelect={handleSelect}
        onRelease={websiteId ? setPendingRelease : undefined}
        open={open}
        onOpenChange={onOpenChange}
      />

      <ClaimSubdomainModal
        isOpen={!!pendingClaim}
        onClose={handleClaimClose}
        onConfirm={handleClaimConfirm}
        domain={pendingClaim?.domain ?? ""}
        creditsRemaining={creditsRemaining === Infinity ? 0 : creditsRemaining}
        unlimited={isUnlimited}
        isLoading={createDomain.isPending}
      />

      <ReleaseDomainModal
        isOpen={!!pendingRelease}
        onClose={handleReleaseClose}
        onConfirm={handleReleaseConfirm}
        domain={pendingRelease?.domain ?? ""}
        isPlatformSubdomain={pendingRelease?.is_platform_subdomain ?? false}
        isLoading={releaseDomain.isPending}
      />
    </div>
  );
}
